import { ConfigFieldType, Schema } from './config'

export interface DiscoveryReport {
  database: string | null
  servers: string[]
  databases: string[]
  templates: string[]
  categories: string[]
  plant_candidates: string[]
  tag_count: number
  capped: boolean
  sample_tags?: string[]
}

export const ConnectorEnum = {
  GENERIC: 'generic',
  SQL: 'sql',
  PI: 'pi',
  SAP: 'sap',
  // Platform-provisioned Fabric Eventhouse (self-service). Backend creates a
  // real Eventhouse via the service principal, seeds dummy data, and links it
  // through the /v2/eventhouse readings path. Option is hidden unless the
  // eventhouse_readings_enabled flag probe passes for the tenant.
  EVENTHOUSE: 'eventhouse',
} as const

export type ConnectorEnumType =
  (typeof ConnectorEnum)[keyof typeof ConnectorEnum]

export const ConnectorStatus = {
  ONLINE: 'online',
  DISCONNECTED: 'disconnected',
} as const

export type ConnectorStatusType =
  (typeof ConnectorStatus)[keyof typeof ConnectorStatus]

// Server-derived liveness (backend/web/src/connectors/connector-health.ts).
// Optional so a response from an API that predates the field still typechecks;
// getConnectorStatus falls back to the same rule locally.
export type ConnectorHealthStatus =
  | 'online'
  | 'stale'
  | 'never_connected'
  | 'inactive'

export interface ConnectorHealth {
  status: ConnectorHealthStatus
  seconds_since_last_connection: number | null
  expected_interval_seconds: number
  grace_seconds: number
}

export interface ConnectorRecord {
  id: string // UUID
  name: string
  description: string
  type: ConnectorEnumType
  created_at: string // datetime | None
  created_by: string
  tenant_id: string // UUID
  interval_connection: number
  last_connection: string // datetime
  config: Record<string, unknown>
  config_timestamp: string // datetime
  active: boolean
  discovery_report?: DiscoveryReport | null
  discovery_report_at?: string | null
  discover_requested_at?: string | null
  discover_database?: string | null
  discover_scope?: Record<string, unknown> | null
  health?: ConnectorHealth
}

export const defaultConnectorRecord = (): ConnectorRecord => ({
  id: '',
  name: '',
  description: '',
  type: 'generic',
  created_at: '',
  created_by: '',
  tenant_id: '',
  interval_connection: 1,
  last_connection: '',
  config: {},
  config_timestamp: '',
  active: false,
})

// The connector's read lane. "batch" = windowed Archive reads; "live" = PI Web API
// Stream Updates (Option A). The mode decides which config knobs make sense, so the
// modal renders a different field set per mode (see piConfigSchema).
export const ConnectorMode = {
  BATCH: 'batch',
  LIVE: 'live',
} as const
export type ConnectorModeType = (typeof ConnectorMode)[keyof typeof ConnectorMode]

// Fields common to BOTH read modes. `tags` is an optional manual override — AF
// auto-discovery fills it, but an operator can still pin/add specific tags.
const PI_COMMON_FIELDS: Schema = {
  plant: {
    type: ConfigFieldType.STRING,
    required: false,
    description: 'Plant prefix written into historian documents (optional)',
  },
  tags: {
    type: ConfigFieldType.STRING,
    required: false,
    description:
      'Comma-separated PI tags (optional — AF discovery fills this; set to pin/override specific tags)',
  },
}

// PI config schema is MODE-AWARE: the windowed-read knobs only apply to batch mode,
// and stream_poll_interval only to live mode, so the read path and modal stay in sync.
export const piConfigSchema = (mode: string): Schema => {
  if ((mode || ConnectorMode.BATCH).toLowerCase() === ConnectorMode.LIVE) {
    return {
      ...PI_COMMON_FIELDS,
      stream_poll_interval: {
        type: ConfigFieldType.STRING,
        required: false,
        defaultValue: '1s',
        description:
          'Live poll cadence — delivery latency, not a sampling rate (e.g. 1s). Advanced.',
      },
    }
  }
  return {
    ...PI_COMMON_FIELDS,
    pull_interval: {
      type: ConfigFieldType.STRING,
      required: false,
      defaultValue: '1h',
      description: 'Time window fetched per pull cycle (e.g. 1h, 30m)',
    },
    pull_delay: {
      type: ConfigFieldType.STRING,
      required: false,
      defaultValue: '5m',
      description: 'Lag behind wall-clock before pulling (e.g. 5m)',
    },
    read_type: {
      type: ConfigFieldType.STRING,
      required: false,
      defaultValue: 'interpolated',
      description: 'PI read type: interpolated (default) or recorded',
    },
    interval: {
      type: ConfigFieldType.STRING,
      required: false,
      defaultValue: '1m',
      description: 'Sample interval for interpolated reads (grid spacing, e.g. 1m)',
    },
    start_from: {
      type: ConfigFieldType.STRING,
      required: false,
      description: 'ISO-8601 datetime to start backfill from (optional)',
    },
    max_points_per_upload: {
      type: ConfigFieldType.NUMBER,
      required: false,
      description: 'Max data-points per upload batch (optional)',
    },
    // document_id is not an operator input: the connector creates a stable
    // "PI Live — <plant>" document on its first pull and pins the id to the row's
    // config itself (PR5/TIME-289).
  }
}

// Connector types offered in the create modal. EVENTHOUSE appears only when the
// availability probe confirms the tenant's flag-gated /v2/eventhouse surface is
// open (null = unprobed → hidden, so a slow probe can't flash the option).
//
// It deliberately does NOT hide the type once the tenant has one. Removing the
// option was indistinguishable from the feature not existing: a user whose only
// eventhouse had FAILED to provision saw no EventHouse option, no reason, and no
// hint that deleting or retrying the failed row was the way forward. The modal
// now renders it disabled with `eventhouseDisabledReason` instead.
export const visibleConnectorTypes = (
  eventhouseAvailable: boolean | null
): ConnectorEnumType[] =>
  Object.values(ConnectorEnum).filter(
    (value) => value !== ConnectorEnum.EVENTHOUSE || eventhouseAvailable === true
  )

/**
 * Why the EventHouse option is unselectable, or null when it is selectable.
 *
 * One per tenant for now: each provisions a real Fabric item. The server
 * enforces it with a 409 — this exists so the UI can say so out loud rather
 * than presenting a dead end or a vanished option.
 */
export const eventhouseDisabledReason = (
  alreadyHasEventhouse: boolean,
  existingStatus?: EventhouseProvisionStatusType | null
): 'limit' | 'limit_failed' | null => {
  if (!alreadyHasEventhouse) return null
  // A failed one still occupies the single slot, and that is the case a user is
  // most likely to be confused by — name it separately so the copy can tell
  // them to retry or delete it.
  return existingStatus === 'error' ? 'limit_failed' : 'limit'
}

export const EventhouseProvisionStatus = {
  QUEUED: 'queued',
  PROVISIONING: 'provisioning',
  SEEDED: 'seeded',
  LINKED: 'linked',
  ERROR: 'error',
} as const
export type EventhouseProvisionStatusType =
  (typeof EventhouseProvisionStatus)[keyof typeof EventhouseProvisionStatus]

export interface EventhouseProvisionState {
  status: EventhouseProvisionStatusType
  error?: string
  connectionId?: string
  documentId?: string
}

// What the UI should SHOW for a provisioning state. A failed link deliberately
// leaves status='seeded' server-side (link is idempotent, so a retry can still
// succeed) with provision_error recorded — but rendering that as "Linking…"
// would hide a stalled connector behind a hopeful spinner and offer no Retry.
// Any recorded error therefore displays, and retries, as ERROR.
export const displayProvisionStatus = (
  state: EventhouseProvisionState
): EventhouseProvisionStatusType =>
  state.error ? EventhouseProvisionStatus.ERROR : state.status

// Read the provisioning state out of an eventhouse connector's config jsonb.
// Missing/unknown status reads as ERROR so a malformed row gets a visible
// Retry surface instead of a silent spinner.
export const getEventhouseProvisionState = (
  connector: ConnectorRecord
): EventhouseProvisionState => {
  const config = connector.config as {
    provision_status?: string
    provision_error?: string
    eventhouse_connection_id?: string
    document_id?: string
  }
  const known = Object.values(EventhouseProvisionStatus) as string[]
  const status = known.includes(config.provision_status ?? '')
    ? (config.provision_status as EventhouseProvisionStatusType)
    : EventhouseProvisionStatus.ERROR
  return {
    status,
    error: config.provision_error,
    connectionId: config.eventhouse_connection_id,
    documentId: config.document_id,
  }
}

// Seed parameters for the platform-provisioned Eventhouse. Bounds are
// enforced server-side (tags 1-50, days 1-90, interval 1-60); descriptions
// keep the form self-explanatory for the dummy-data phase (no live PI yet).
export const eventhouseConfigSchema: Schema = {
  table_name: {
    type: ConfigFieldType.STRING,
    required: false,
    defaultValue: 'readings',
    description: 'Kusto table to create and seed (default: readings)',
  },
  tag_count: {
    type: ConfigFieldType.NUMBER,
    required: false,
    defaultValue: 4,
    description: 'How many demo tags to generate (1-50)',
  },
  days: {
    type: ConfigFieldType.NUMBER,
    required: false,
    defaultValue: 7,
    description: 'Days of history to seed (1-90)',
  },
  interval_minutes: {
    type: ConfigFieldType.NUMBER,
    required: false,
    defaultValue: 1,
    description: 'Minutes between samples (1-60)',
  },
  tag_prefix: {
    type: ConfigFieldType.STRING,
    required: false,
    defaultValue: 'DEMO-TAG',
    description: 'Tag id prefix — tags become PREFIX-1, PREFIX-2, …',
  },
}

export const connectorConfigSchema: Record<ConnectorEnumType, Schema> = {
  [ConnectorEnum.GENERIC]: {},
  [ConnectorEnum.SQL]: {
    connection_string: {
      type: ConfigFieldType.STRING,
      required: true,
      description: 'The connection string for the SQL database',
    },
  },
  // Static entry = the batch default; the modal swaps in piConfigSchema(mode) for PI.
  [ConnectorEnum.PI]: piConfigSchema(ConnectorMode.BATCH),
  [ConnectorEnum.SAP]: {}, // TODO: Add SAP config schema
  [ConnectorEnum.EVENTHOUSE]: eventhouseConfigSchema,
}

// Effective config schema for a connector, accounting for PI's mode-aware fields.
export const effectiveConnectorSchema = (
  type: ConnectorEnumType,
  mode: string
): Schema =>
  type === ConnectorEnum.PI ? piConfigSchema(mode) : connectorConfigSchema[type]
